/**
 * The WebGL2 path.
 *
 * This is the same affine as `core/compose.ts` produces, run on the GPU. It is
 * not a second algorithm: `M` and `b` come from `collapse()` unchanged, and the
 * fragment shader evaluates `clamp(M x + b, 0, 255)` once per pixel. The whole
 * plan hypothesis (T2) is that this is bit-for-bit close enough to the CPU path
 * to be a drop-in replacement, so `tools/gl-parity.ts` measures the difference
 * rather than trusting it.
 *
 * # Two details that are easy to get wrong
 *
 * **Column-major.** Our `Mat3` is `m[col * 3 + row]`, matching GLSL `mat3`, so
 * the array goes straight into `uniformMatrix3fv(loc, false, m)` with
 * `transpose = false`. Uploading a transpose would look plausible and be wrong.
 *
 * **Teardown.** The affine is built in `float64`, but the GPU sees `float32`.
 * If the CPU path kept the `float64` values, the comparison would measure the
 * quantisation as well as the shader. `quantise()` exists so the caller can take
 * one `Float32Array` pair and feed *both* paths from it.
 *
 * @module web/js/gl
 */

import type { Affine, Raster } from '../../core/types.ts';
import type { Lut3d } from '../../core/luts.ts';

/**
 * The affine exactly as the GPU will see it.
 *
 * `M` is column-major and `Float32Array` — the same array that is handed to
 * `uniformMatrix3fv`. `b` is the offset, also `Float32Array`.
 */
export interface GlAffine {
  M: Float32Array;
  B: Float32Array;
}

/**
 * Round an affine to the precision the GPU uses.
 *
 * Call this **once**, then build the CPU reference from the result as well. If
 * the CPU path runs from the original `float64` values, the parity test measures
 * the quantisation error instead of the shader, and the tolerances stop meaning
 * anything.
 */
export function quantise(affine: Affine): GlAffine {
  return { M: new Float32Array(affine.M), B: new Float32Array(affine.b) };
}

/**
 * The CPU reference for a quantised affine.
 *
 * Widens the `Float32Array` back to `float64` so that the CPU path does its
 * arithmetic in double precision on exactly the numbers the GPU was given. That
 * is the only way the comparison isolates the GPU's own error.
 */
export function dequantise(affine: GlAffine): Affine {
  return { M: Float64Array.from(affine.M), b: Float64Array.from(affine.B) };
}

/**
 * Vertex shader: one oversized triangle covering the viewport.
 *
 * `gl_VertexID` means no attribute buffers are needed. The triangle spans
 * (-1,-1)..(3,3) so its edges lie outside the clip volume.
 */
const VERTEX_SOURCE = `#version 300 es
void main() {
  vec2 p = vec2[3](vec2(-1.0, -1.0), vec2(3.0, -1.0), vec2(-1.0, 3.0))[gl_VertexID];
  gl_Position = vec4(p, 0.0, 1.0);
}`;

/**
 * Fragment shader: the whole transform.
 *
 * `uv` is derived from `gl_FragCoord` divided by `uOutSize`, the drawing
 * buffer's size, not from an interpolated varying. When the drawing buffer and
 * the source are the same size — the parity case — fragment `(i, j)` samples
 * texel `(i, j)` exactly, with no half-pixel drift and no dependence on the
 * triangle's rasterisation. When they differ, as on the camera page, the whole
 * viewport maps onto the whole source instead of sampling one corner of it.
 *
 * The sampled value is scaled to 0..255 before the multiply, so `uM` and `uB`
 * are the same numbers the CPU path uses. Doing the arithmetic in 0..1 would
 * need a different `b` and would be a silent source of disagreement.
 *
 * No sRGB conversion happens anywhere: the texture is plain `RGBA8`, so byte `v`
 * samples as `v / 255` and the result is written back as `v / 255`. The
 * stretch is defined on the byte values, and this keeps it there.
 */
const FRAGMENT_SOURCE = `#version 300 es
precision highp float;
precision highp sampler2D;

uniform sampler2D uTex;
uniform mat3 uM;
uniform vec3 uB;
uniform vec2 uOutSize;

out vec4 outColor;

void main() {
  vec2 uv = gl_FragCoord.xy / uOutSize;
  vec3 x = texture(uTex, uv).rgb * 255.0;
  vec3 y = clamp(uM * x + uB, 0.0, 255.0);
  outColor = vec4(y / 255.0, 1.0);
}`;

/**
 * Fragment shader for a nonlinear preset, evaluated through a 3D LUT.
 *
 * The LUT is uploaded as a `sampler3D` of 8-bit texels, one texel per grid
 * point. `LINEAR` filtering on a 3D texture is trilinear interpolation, so the
 * hardware does exactly what `core/luts.ts:sample3dLut` does in software.
 *
 * The coordinate that makes them agree is `(f + 0.5) / n`, where `f` is the
 * fractional grid position: texel centres sit at `(i + 0.5) / n`, so this maps
 * grid point `i` onto texel `i` and scales the interpolation weight with it.
 * Sampling at `f / n` instead would be half a texel off and would show.
 *
 * The texels hold 0..255 bytes dequantised by `uLutMin`/`uLutMax`, mirroring
 * `lut3dValue`. The result is the same `clamp(v * 255, 0, 255)` the CPU applies.
 */
const LUT_FRAGMENT_SOURCE = `#version 300 es
precision highp float;
precision highp sampler2D;
precision highp sampler3D;

uniform sampler2D uTex;
uniform sampler3D uLut;
uniform vec3 uLutMin;
uniform vec3 uLutMax;
uniform float uLutN;
uniform vec2 uOutSize;

out vec4 outColor;

void main() {
  vec2 uv = gl_FragCoord.xy / uOutSize;
  vec3 x = clamp(texture(uTex, uv).rgb, 0.0, 1.0);
  vec3 f = x * (uLutN - 1.0);
  vec3 uvw = (f + 0.5) / uLutN;
  vec3 t = texture(uLut, uvw).rgb;
  vec3 v = uLutMin + (uLutMax - uLutMin) * t;
  vec3 y = clamp(v * 255.0, 0.0, 255.0);
  outColor = vec4(y / 255.0, 1.0);
}`;

/** Arguments to `GlRenderer.drawElement`. */
export interface ElementDrawOptions {
  /** The frame to upload: a `<video>`, an `<img>`, or a canvas. */
  source: TexImageSource;
  /** The source's pixel size. The texture is allocated at this size. */
  sourceWidth: number;
  sourceHeight: number;
  /** Drawing-buffer size in device pixels. Defaults to the source size. */
  width?: number;
  height?: number;
  /**
   * Flip the source vertically on upload. Default `true`, which is what a DOM
   * source needs; pass `false` only for a source that is already bottom-row
   * first.
   */
  flipY?: boolean;
}

/** Raised when WebGL2 is unavailable or a shader fails to build. */
export class GlUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GlUnavailableError';
  }
}

function compile(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new GlUnavailableError('createShader returned null');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader) ?? '(no log)';
    gl.deleteShader(shader);
    throw new GlUnavailableError(`shader compile failed: ${log}`);
  }
  return shader;
}

function link(gl: WebGL2RenderingContext, fragmentSource: string): WebGLProgram {
  const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX_SOURCE);
  const fragment = compile(gl, gl.FRAGMENT_SHADER, fragmentSource);
  const program = gl.createProgram();
  if (!program) throw new GlUnavailableError('createProgram returned null');
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  // The shaders are owned by the program once linked.
  gl.deleteShader(vertex);
  gl.deleteShader(fragment);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program) ?? '(no log)';
    gl.deleteProgram(program);
    throw new GlUnavailableError(`program link failed: ${log}`);
  }
  return program;
}

/** A resizable RGBA8 texture. */
interface Image {
  texture: WebGLTexture;
  width: number;
  height: number;
}

/** A 3D LUT uploaded as a `sampler3D`, with the LUT it was built from. */
interface LutImage {
  source: Lut3d;
  texture: WebGLTexture;
}

/** Locations in the affine program. */
interface AffineUniforms {
  tex: WebGLUniformLocation | null;
  m: WebGLUniformLocation | null;
  b: WebGLUniformLocation | null;
  out: WebGLUniformLocation | null;
}

/** Locations in the 3D-LUT program. */
interface LutUniforms {
  tex: WebGLUniformLocation | null;
  lut: WebGLUniformLocation | null;
  min: WebGLUniformLocation | null;
  max: WebGLUniformLocation | null;
  n: WebGLUniformLocation | null;
  out: WebGLUniformLocation | null;
}

function makeImage(gl: WebGL2RenderingContext): Image {
  const texture = gl.createTexture();
  if (!texture) throw new GlUnavailableError('createTexture returned null');
  return { texture, width: 0, height: 0 };
}

function uploadImage(gl: WebGL2RenderingContext, image: Image, raster: Raster): void {
  gl.bindTexture(gl.TEXTURE_2D, image.texture);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
  // No UNPACK_FLIP_Y: with a bottom-left origin throughout, the first row of
  // `raster.data` becomes framebuffer row 0, and `readPixels` hands row 0 back
  // first. Upload and readback therefore use the same convention and cancel.
  if (image.width !== raster.width || image.height !== raster.height) {
    gl.texImage2D(
      gl.TEXTURE_2D, 0, gl.RGBA8, raster.width, raster.height, 0,
      gl.RGBA, gl.UNSIGNED_BYTE, raster.data,
    );
    image.width = raster.width;
    image.height = raster.height;
  } else {
    gl.texSubImage2D(
      gl.TEXTURE_2D, 0, 0, 0, raster.width, raster.height,
      gl.RGBA, gl.UNSIGNED_BYTE, raster.data,
    );
  }
  // NEAREST, not LINEAR: a filter would blend neighbouring pixels and the
  // parity test would then be measuring the filter, not the shader.
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
}

/**
 * The GPU renderer.
 *
 * Owns a canvas, a program, and two texture pairs: one for the source image and
 * one to render into. The render target is a texture rather than the canvas so
 * that `readPixels` never depends on `preserveDrawingBuffer`, which is slow and
 * unreliable in headless Chrome.
 */
export class GlRenderer {
  readonly canvas: HTMLCanvasElement;
  readonly gl: WebGL2RenderingContext;

  private readonly program: WebGLProgram;
  private readonly uniforms: AffineUniforms;
  private readonly lutProgram: WebGLProgram;
  private readonly lutUniforms: LutUniforms;

  private readonly source: Image;
  private readonly target: Image;
  private readonly framebuffer: WebGLFramebuffer;
  private readonly vao: WebGLVertexArrayObject | null;
  /** Scratch buffer for `readPixels`, grown as needed. */
  private readback = new Uint8Array(0);
  /** The 3D LUT texture, rebuilt only when a different LUT is drawn. */
  private lutImage: LutImage | null = null;

  constructor(canvas: HTMLCanvasElement = document.createElement('canvas')) {
    this.canvas = canvas;
    const gl = canvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      // Everything here is integer RGBA8; there is no colour management to do,
      // and asking for it would make the GPU's rounding differ from the CPU's.
      premultipliedAlpha: false,
      preserveDrawingBuffer: false,
    });
    if (!gl) {
      throw new GlUnavailableError(
        'no WebGL2 context. In headless Chrome this needs BOTH --use-angle=swiftshader and --enable-unsafe-swiftshader',
      );
    }
    this.gl = gl;
    this.program = link(gl, FRAGMENT_SOURCE);
    this.uniforms = {
      tex: gl.getUniformLocation(this.program, 'uTex'),
      m: gl.getUniformLocation(this.program, 'uM'),
      b: gl.getUniformLocation(this.program, 'uB'),
      out: gl.getUniformLocation(this.program, 'uOutSize'),
    };
    // The LUT program is built eagerly. If the driver cannot compile it, the
    // failure is better raised here, where the camera card can fall back, than
    // on the first tap of the LAB chip.
    this.lutProgram = link(gl, LUT_FRAGMENT_SOURCE);
    this.lutUniforms = {
      tex: gl.getUniformLocation(this.lutProgram, 'uTex'),
      lut: gl.getUniformLocation(this.lutProgram, 'uLut'),
      min: gl.getUniformLocation(this.lutProgram, 'uLutMin'),
      max: gl.getUniformLocation(this.lutProgram, 'uLutMax'),
      n: gl.getUniformLocation(this.lutProgram, 'uLutN'),
      out: gl.getUniformLocation(this.lutProgram, 'uOutSize'),
    };
    this.source = makeImage(gl);
    this.target = makeImage(gl);

    const framebuffer = gl.createFramebuffer();
    if (!framebuffer) throw new GlUnavailableError('createFramebuffer returned null');
    this.framebuffer = framebuffer;

    // No attributes are read, but a VAO must still be bound.
    this.vao = gl.createVertexArray();
    gl.bindVertexArray(this.vao);

    gl.disable(gl.BLEND);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.DITHER);
    gl.disable(gl.SCISSOR_TEST);
    // Straight-ish: the output is written as an RGBA8 image and read back
    // verbatim, so no fragment discard or coverage rule may interfere.
    gl.colorMask(true, true, true, true);
  }

  /**
   * Bind the program, upload the source and set the uniforms.
   *
   * The drawing buffer is the raster's own size here, so `uOutSize` makes `uv`
   * a 1:1 texel mapping — the same mapping the old `textureSize()` form gave,
   * now stated rather than inferred.
   */
  private prepare(raster: Raster, affine: GlAffine): void {
    const gl = this.gl;
    uploadImage(gl, this.source, raster);
    gl.useProgram(this.program);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.source.texture);
    gl.uniform1i(this.uniforms.tex, 0);
    gl.uniform2f(this.uniforms.out, raster.width, raster.height);
    gl.uniformMatrix3fv(this.uniforms.m, false, affine.M);
    gl.uniform3fv(this.uniforms.b, affine.B);
  }

  /** Point the render target texture at an FBO and size the viewport. */
  private bindTarget(width: number, height: number): void {
    const gl = this.gl;
    // Bind on a unit nothing samples. `TEXTURE0` still holds the source from
    // `prepare()`, and binding the target there would leave the shader reading
    // the framebuffer it is writing: GL_INVALID_OPERATION, "feedback loop".
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.target.texture);
    if (this.target.width !== width || this.target.height !== height) {
      // `null` data allocates without a source buffer; the shader writes every
      // texel, so there is nothing to clear.
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      this.target.width = width;
      this.target.height = height;
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.target.texture, 0);
    const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
    if (status !== gl.FRAMEBUFFER_COMPLETE) {
      throw new GlUnavailableError(`framebuffer incomplete: 0x${status.toString(16)}`);
    }
    gl.viewport(0, 0, width, height);
  }

  private drawQuad(): void {
    const gl = this.gl;
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  /** Run a raster through `affine` and return fresh RGBA bytes. */
  renderPixels(raster: Raster, affine: GlAffine): Uint8Array {
    const gl = this.gl;
    const { width, height } = raster;
    this.prepare(raster, affine);
    this.bindTarget(width, height);
    this.drawQuad();

    const needed = width * height * 4;
    if (this.readback.length < needed) this.readback = new Uint8Array(needed);
    const out = new Uint8Array(this.readback.buffer, 0, needed);
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, out);
    const error = gl.getError();
    if (error !== gl.NO_ERROR) throw new GlUnavailableError(`GL error after readPixels: 0x${error.toString(16)}`);
    // Copy so the caller owns its bytes and the scratch buffer can be reused.
    return out.slice();
  }

  /**
   * Upload a DOM source and point the drawing buffer at it.
   *
   * Shared by `drawElement` and `drawElementLut`, because the only difference
   * between them is the program. Nothing goes through `getImageData`, so there
   * is no CPU copy of the frame and no per-frame allocation.
   *
   * Three things to note:
   *
   *   - **`UNPACK_FLIP_Y_WEBGL` is on.** A `Raster` is laid out like `ImageData`,
   *     whose first row the upload path treats as the texture's bottom row; the
   *     readback and the display then cancel out. A DOM source is drawn
   *     top-row-first, so without the flip the picture on screen is upside down.
   *   - **`LINEAR` filtering.** The source is usually a different size from the
   *     drawing buffer and the GPU is doing the scaling. `NEAREST` would alias
   *     badly on a moving picture. The parity test keeps `NEAREST` because there
   *     the two sides must agree byte for byte.
   *   - **Two sizes.** The texture must be the *source's* size, because
   *     `texSubImage2D` copies the whole source into it. The viewport and the
   *     canvas must be the *drawing buffer's* size. The caller sets `uOutSize`
   *     to the drawing-buffer size, so the viewport maps onto the full source
   *     rather than onto its top-left corner.
   */
  private uploadElementSource(opts: ElementDrawOptions): { width: number; height: number } {
    const gl = this.gl;
    const width = Math.max(1, Math.floor(opts.width ?? opts.sourceWidth));
    const height = Math.max(1, Math.floor(opts.height ?? opts.sourceHeight));
    const sw = Math.max(1, Math.floor(opts.sourceWidth));
    const sh = Math.max(1, Math.floor(opts.sourceHeight));

    // Writing `width`/`height` reallocates and clears the drawing buffer even
    // when the value is unchanged, and this runs once per video frame, so only
    // write it when it actually differs.
    if (this.canvas.width !== width) this.canvas.width = width;
    if (this.canvas.height !== height) this.canvas.height = height;

    const image = this.source;
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, image.texture);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, opts.flipY ?? true);

    if (image.width === sw && image.height === sh) {
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, gl.RGBA, gl.UNSIGNED_BYTE, opts.source);
    } else {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, opts.source);
      image.width = sw;
      image.height = sh;
    }

    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    // Leave the pixel store as the raster path expects it.
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, width, height);
    return { width, height };
  }

  /**
   * Draw one frame straight from a DOM source through the affine shader.
   *
   * See `uploadElementSource` for the upload's own rules. This sets `uOutSize`
   * to the drawing buffer, so a source larger than the buffer is scaled down
   * over the whole frame instead of being cropped to its top-left corner.
   */
  drawElement(affine: GlAffine, opts: ElementDrawOptions): void {
    const gl = this.gl;
    const { width, height } = this.uploadElementSource(opts);

    gl.useProgram(this.program);
    gl.uniform1i(this.uniforms.tex, 0);
    gl.uniform2f(this.uniforms.out, width, height);
    gl.uniformMatrix3fv(this.uniforms.m, false, affine.M);
    gl.uniform3fv(this.uniforms.b, affine.B);

    this.drawQuad();
  }

  /**
   * Draw one frame through a nonlinear preset, given as a 3D LUT.
   *
   * This is how LAB runs at video rate: the 32-cubed table is uploaded once as
   * a `sampler3D`, and the hardware's trilinear filter does the interpolation
   * that `sample3dLut` would otherwise do in JavaScript, per pixel, per frame.
   */
  drawElementLut(lut: Lut3d, opts: ElementDrawOptions): void {
    const gl = this.gl;
    const { width, height } = this.uploadElementSource(opts);

    gl.useProgram(this.lutProgram);
    // Unit 0: the video frame.
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.source.texture);
    gl.uniform1i(this.lutUniforms.tex, 0);
    // Unit 2: the table. Unit 1 belongs to the render target, and binding the
    // LUT there would put it on a texture the render path may later attach.
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_3D, this.lutTexture(lut));
    gl.uniform1i(this.lutUniforms.lut, 2);
    gl.uniform3fv(this.lutUniforms.min, new Float32Array(lut.min));
    gl.uniform3fv(this.lutUniforms.max, new Float32Array(lut.max));
    gl.uniform1f(this.lutUniforms.n, lut.size);
    gl.uniform2f(this.lutUniforms.out, width, height);

    this.drawQuad();
    gl.activeTexture(gl.TEXTURE0);
  }

  /**
   * Upload `lut` as a `sampler3D`, reusing the last upload when it is the same
   * table. The camera page rebuilds its matrix every frame but the LUT never
   * changes, so the upload happens once per renderer.
   */
  private lutTexture(lut: Lut3d): WebGLTexture {
    if (this.lutImage && this.lutImage.source === lut) return this.lutImage.texture;
    const gl = this.gl;
    if (this.lutImage) gl.deleteTexture(this.lutImage.texture);
    const texture = gl.createTexture();
    if (!texture) throw new GlUnavailableError('createTexture returned null for the 3D LUT');

    const n = lut.size;
    const count = n * n * n;
    // The LUT's own index is `((r * n + g) * n + b) * 3 + c`, which is exactly
    // the linear texel order of a 3D texture with x fastest: x = b, y = g,
    // z = r. Expanding RGB to RGBA keeps the rows aligned and lets a plain
    // RGBA8 internal format be used.
    const rgba = new Uint8Array(count * 4);
    for (let i = 0; i < count; i++) {
      rgba[i * 4] = lut.data[i * 3]!;
      rgba[i * 4 + 1] = lut.data[i * 3 + 1]!;
      rgba[i * 4 + 2] = lut.data[i * 3 + 2]!;
      rgba[i * 4 + 3] = 255;
    }

    gl.bindTexture(gl.TEXTURE_3D, texture);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    // A 3D upload must not be flipped; `texImage3D` ignores the flag, but a
    // stale `true` would confuse the next 2D upload.
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.texImage3D(gl.TEXTURE_3D, 0, gl.RGBA8, n, n, n, 0, gl.RGBA, gl.UNSIGNED_BYTE, rgba);
    // LINEAR, not NEAREST: trilinear filtering is the interpolation.
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);
    gl.bindTexture(gl.TEXTURE_3D, null);

    const error = gl.getError();
    if (error !== gl.NO_ERROR) {
      gl.deleteTexture(texture);
      throw new GlUnavailableError(`3D LUT upload failed: 0x${error.toString(16)}`);
    }
    this.lutImage = { source: lut, texture };
    return texture;
  }

  /** Run a raster through `affine` and leave the result on the canvas. */
  draw(raster: Raster, affine: GlAffine): void {
    const gl = this.gl;
    this.canvas.width = raster.width;
    this.canvas.height = raster.height;
    this.prepare(raster, affine);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, raster.width, raster.height);
    this.drawQuad();
  }

  /** Release every GL object this renderer owns. */
  dispose(): void {
    const gl = this.gl;
    gl.deleteFramebuffer(this.framebuffer);
    gl.deleteTexture(this.source.texture);
    gl.deleteTexture(this.target.texture);
    if (this.lutImage) gl.deleteTexture(this.lutImage.texture);
    gl.deleteProgram(this.program);
    gl.deleteProgram(this.lutProgram);
    if (this.vao) gl.deleteVertexArray(this.vao);
    this.readback = new Uint8Array(0);
    this.lutImage = null;
  }
}

/** True when this browser can make a WebGL2 context at all. */
export function glAvailable(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return canvas.getContext('webgl2') !== null;
  } catch {
    return false;
  }
}
